package com.partejar.app.feature.gestantes.presentation

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.State
import androidx.compose.ui.Alignment
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.compositeOver
import androidx.compose.ui.graphics.luminance
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.graphics.lerp
import androidx.compose.ui.graphics.TransformOrigin
import androidx.compose.ui.layout.boundsInRoot
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.hideFromAccessibility
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.IntOffset
import com.partejar.app.core.ui.components.PartejarIconButton
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.gestantes.data.GestanteClinicalDetail
import com.partejar.app.feature.gestantes.data.GestanteListStage
import com.partejar.app.feature.gestantes.presentation.components.gestationalCompactSubtitle
import com.partejar.app.feature.gestantes.presentation.components.GestationalHero
import com.partejar.app.feature.gestantes.presentation.components.GestationalHeroAnchor
import kotlin.math.abs
import kotlin.math.min
import kotlin.math.roundToInt

internal fun gestanteStatusBarLightIcons(
    hasArtwork: Boolean,
    expandedLayout: Boolean,
    collapseProgress: Float,
    background: Color
): Boolean? = if (hasArtwork && !expandedLayout) {
    // At the status bar the contextual header is 96% opaque when fully compact.
    val effectiveBackground = background.copy(alpha = 0.96f * collapseProgress.coerceIn(0f, 1f))
        .compositeOver(Color.Black)
    // Crossover where black and white have equal contrast against this luminance.
    effectiveBackground.luminance() < 0.179f
} else null

internal fun gestationalRingHandoffProgress(
    ringAnchor: GestationalHeroAnchor?,
    scrollOffset: Int,
    compactHeaderBottom: Float?
): Float {
    if (ringAnchor == null || compactHeaderBottom == null) return 0f
    val ringTopInViewport = ringAnchor.top - scrollOffset
    return ((compactHeaderBottom - ringTopInViewport) / ringAnchor.height.coerceAtLeast(1f))
        .coerceIn(0f, 1f)
}

internal data class GestationalHandoffTransform(
    val x: Float,
    val y: Float,
    val scale: Float
)

internal fun gestationalHandoffSourceBounds(
    contentBounds: Rect?,
    ringAnchor: GestationalHeroAnchor?,
    compactHeaderBottom: Float?
): Rect? {
    if (contentBounds == null || ringAnchor == null || compactHeaderBottom == null) return null
    val scrollAtHandoffStart = ringAnchor.top - compactHeaderBottom
    return Rect(
        left = contentBounds.left,
        top = contentBounds.top - scrollAtHandoffStart,
        right = contentBounds.right,
        bottom = contentBounds.bottom - scrollAtHandoffStart
    )
}

internal fun gestationalHandoffTransform(
    sourceBounds: Rect,
    destinationBounds: Rect,
    progress: Float
): GestationalHandoffTransform? {
    if (sourceBounds.width <= 0f || sourceBounds.height <= 0f ||
        destinationBounds.width <= 0f || destinationBounds.height <= 0f
    ) return null
    val fraction = progress.coerceIn(0f, 1f)
    val sourceScale = min(
        sourceBounds.width / destinationBounds.width,
        sourceBounds.height / destinationBounds.height
    )
    val sourceX = sourceBounds.center.x - (destinationBounds.width * sourceScale / 2f)
    val sourceY = sourceBounds.center.y - (destinationBounds.height * sourceScale / 2f)
    return GestationalHandoffTransform(
        x = sourceX + ((destinationBounds.left - sourceX) * fraction),
        y = sourceY + ((destinationBounds.top - sourceY) * fraction),
        scale = sourceScale + ((1f - sourceScale) * fraction)
    )
}

@Composable
internal fun BoxScope.GestanteBackAction(
    onBack: () -> Unit,
    onMedia: Boolean,
    contextualHeaderAlpha: State<Float>
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    val headerAlpha = contextualHeaderAlpha.value
    Surface(
        modifier = Modifier
            .align(Alignment.TopStart)
            .windowInsetsPadding(
                WindowInsets.safeDrawing.only(WindowInsetsSides.Top + WindowInsetsSides.Start)
            )
            .padding(start = spacing.space16, top = spacing.space8),
        shape = MaterialTheme.shapes.large,
        color = if (onMedia) {
            MaterialTheme.colorScheme.scrim.copy(alpha = 0.28f * (1f - headerAlpha))
        } else {
            MaterialTheme.colorScheme.surfaceContainerLow
        },
        contentColor = if (onMedia) {
            lerp(Color.White, MaterialTheme.colorScheme.onBackground, headerAlpha)
        } else {
            MaterialTheme.colorScheme.onBackground
        }
    ) {
        PartejarIconButton(
            onClick = onBack,
            modifier = Modifier.size(dimensions.minimumTouchTarget)
        ) {
            Icon(PartejarIcons.Actions.back, contentDescription = "Voltar")
        }
    }
}

@Composable
internal fun GestanteIdentification(
    detail: GestanteClinicalDetail,
    identityAlpha: State<Float>?,
    ringHandoffProgress: State<Float>?,
    onIdentityPositioned: (GestationalHeroAnchor) -> Unit,
    onProgressRingPositioned: (GestationalHeroAnchor) -> Unit,
    onProgressContentPositioned: (Rect) -> Unit,
    showStatusBadge: Boolean = true
) {
    GestationalHero(
        name = detail.name,
        babyName = detail.babyName,
        weeks = detail.gestationalWeeks,
        days = detail.gestationalDays,
        dueDate = detail.dueDate,
        active = detail.stage == GestanteListStage.ACTIVE,
        showStatusBadge = showStatusBadge,
        identityAlpha = identityAlpha,
        ringHandoffProgress = ringHandoffProgress,
        onIdentityPositioned = onIdentityPositioned,
        onProgressRingPositioned = onProgressRingPositioned,
        onProgressContentPositioned = onProgressContentPositioned
    )
}

@Composable
internal fun BoxScope.GestanteContextualHeader(
    detail: GestanteClinicalDetail,
    collapseProgress: State<Float>,
    ringHandoffProgress: State<Float>,
    onAgePositioned: (Rect) -> Unit,
    onPositioned: (androidx.compose.ui.layout.LayoutCoordinates) -> Unit
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    val surfaceAlpha = collapseProgress.value
    val contentAlpha = (surfaceAlpha * 1.35f).coerceAtMost(1f)
    val ageHandoff = ringHandoffProgress.value
    val destinationReveal = ((ageHandoff - 0.82f) / 0.18f).coerceIn(0f, 1f)
    val babySubtitle = detail.babyName?.trim()?.takeIf { it.isNotEmpty() }?.let { "Bebê $it" }
    val ageSubtitle = gestationalCompactSubtitle(null, detail.gestationalWeeks, detail.gestationalDays)

    Box(
        modifier = Modifier
            .align(Alignment.TopStart)
            .fillMaxWidth()
            .background(
                Brush.verticalGradient(
                    colorStops = arrayOf(
                        0f to MaterialTheme.colorScheme.background.copy(alpha = 0.96f * surfaceAlpha),
                        0.42f to MaterialTheme.colorScheme.background.copy(alpha = 0.84f * surfaceAlpha),
                        0.76f to MaterialTheme.colorScheme.background.copy(alpha = 0.50f * surfaceAlpha),
                        1f to Color.Transparent
                    )
                )
            )
            .onGloballyPositioned(onPositioned)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .then(
                    if (contentAlpha <= 0.5f) {
                        Modifier.semantics { hideFromAccessibility() }
                    } else {
                        Modifier
                    }
                )
                .windowInsetsPadding(
                    WindowInsets.safeDrawing.only(
                        WindowInsetsSides.Top + WindowInsetsSides.Horizontal
                    )
                )
                .padding(
                    start = spacing.space16 + dimensions.minimumTouchTarget + spacing.space8,
                    end = spacing.space16,
                    top = spacing.space8,
                    bottom = spacing.space8
                )
                .graphicsLayer { alpha = contentAlpha },
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                Text(
                    detail.name,
                    style = MaterialTheme.typography.titleMedium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                if (babySubtitle != null || ageSubtitle != null) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        babySubtitle?.let {
                            Text(
                                it,
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                        ageSubtitle?.let {
                            Box(
                                modifier = Modifier.onGloballyPositioned { coordinates ->
                                    onAgePositioned(coordinates.boundsInRoot())
                                }
                            ) {
                                Text(
                                    text = if (babySubtitle == null) it else " · $it",
                                    modifier = Modifier
                                        .then(
                                            if (destinationReveal <= 0.5f) {
                                                Modifier.semantics { hideFromAccessibility() }
                                            } else {
                                                Modifier
                                            }
                                        )
                                        .graphicsLayer {
                                            alpha = destinationReveal
                                            val scale = 0.96f + (0.04f * destinationReveal)
                                            scaleX = scale
                                            scaleY = scale
                                        },
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
internal fun BoxScope.GestationalAgeHandoffOverlay(
    detail: GestanteClinicalDetail,
    progress: Float,
    sourceBounds: Rect?,
    destinationBounds: Rect?
) {
    val age = gestationalCompactSubtitle(null, detail.gestationalWeeks, detail.gestationalDays) ?: return
    val source = sourceBounds ?: return
    val destination = destinationBounds ?: return
    if (progress <= 0f || progress >= 1f) return
    val transform = gestationalHandoffTransform(source, destination, progress) ?: return
    val hasBabyName = detail.babyName?.trim()?.isNotEmpty() == true
    val text = if (hasBabyName) " · $age" else age
    val entranceAlpha = ((progress - 0.02f) / 0.14f).coerceIn(0f, 1f)
    val exitAlpha = ((1f - progress) / 0.18f).coerceIn(0f, 1f)
    val overlayAlpha = min(entranceAlpha, exitAlpha)

    Text(
        text = text,
        modifier = Modifier
            .align(Alignment.TopStart)
            .offset { IntOffset(transform.x.roundToInt(), transform.y.roundToInt()) }
            .semantics { hideFromAccessibility() }
            .graphicsLayer {
                transformOrigin = TransformOrigin(0f, 0f)
                alpha = overlayAlpha
                scaleX = transform.scale
                scaleY = transform.scale
            },
        style = MaterialTheme.typography.bodyMedium,
        color = lerp(Color.White.copy(alpha = 0.94f), MaterialTheme.colorScheme.onSurfaceVariant, progress),
        maxLines = 1
    )
}

internal fun GestationalHeroAnchor?.hasMeaningfulDifferenceFrom(other: GestationalHeroAnchor): Boolean =
    this == null || abs(top - other.top) > 0.5f || abs(bottom - other.bottom) > 0.5f

internal fun Rect?.hasMeaningfulDifferenceFrom(other: Rect): Boolean =
    this == null || abs(left - other.left) > 0.5f || abs(top - other.top) > 0.5f ||
        abs(right - other.right) > 0.5f || abs(bottom - other.bottom) > 0.5f
